import { useQueries } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  type AchTransferBatch,
  type ListApprovalsResponse,
  type SwiftTransfer,
  type TransferReviewBatch,
  type TransferReviewBatchDecision,
  type UnifiedTransfer,
  approvalsListApprovals,
  bankAccountGetBankAccountOptions,
  counterpartyGetCounterpartyOptions,
  transferAchBatchGetBatchOptions,
  transferAchGetTransferOptions,
  transferCheckGetCheckTransferOptions,
  transferRealtimeGetTransferOptions,
  transferSwiftGetTransferOptions,
  transferWireGetWireTransferOptions,
} from '@column/api';
import { Button, Fade, Icon, Input } from '@column/column-ui-kit';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, cn } from '@column/ui';

import {
  type ReviewQuoteTransfer,
  useInternationalWireReviewQuote,
} from '~/app/pages/Transfers/_hooks/useInternationalWireReviewQuote';
import { getInternationalWireReviewFXQuoteDetails } from '~/app/pages/Transfers/_utils/internationalWire';
import {
  type ApprovalHoldRequest,
  isApprovalHold,
  isInternationalWireApprovalBlocked,
} from '~/app/pages/Transfers/_utils/transferReview';
import { ROUTE } from '~/app/routes';
import { CopyRow, CustomFieldWrapper, EntityLink } from '~/components/CopyRow';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { TransferHeader } from '~/lib/transfers';
import { getBatchNetAmount, getTransferURL, isBatchTransfer } from '~/lib/transfers/utils';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { Card, Flex, Text } from '~/styles';
import { TransferReviewDecision } from '~/typings/enum';

import { PageCounter } from './PageCounter';
import { ReviewBatchSummary } from './ReviewBatchSummary';
import { AnimatedHeight, BACKGROUND_NOTICE, ReviewDialog } from './ReviewDialog';

export type ReviewTransferFormValues = ApprovalHoldRequest & { fx_quote_id?: string };

const ReviewQuoteFieldWrapper = styled(CustomFieldWrapper)`
  display: flex;
  align-self: stretch;
  align-items: center;
  padding: 0 4px 0 16px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 0 8px 0 36px;
  }
`;

export interface ReviewListQuery {
  show_batches?: boolean;
}

interface ReviewSingleDialogProps {
  listQuery?: ReviewListQuery;
  transfers: UnifiedTransfer[];
  transferStartIndex: number;
  currentIndex: number;
  totalTransfers: number;
  isOpen: boolean;
  onClose: () => void;
  onCurrentTransferChange?: (transferId: string | undefined) => void;
  unavailableIds?: ReadonlySet<string>;
  onSubmit: (
    transferId: string,
    transferType: TransferEntryType,
    transferStatus: string,
    request: ReviewTransferFormValues
  ) => Promise<TransferReviewBatch | void>;
}

interface BatchReviewState {
  id: string;
  decision: TransferReviewBatchDecision;
  count: number;
}

const MODAL_PAGE_SIZE = 10;
const REVEAL_DELAY_MS = 500;
const extractTransfers = (page: ListApprovalsResponse | undefined): UnifiedTransfer[] =>
  page?.approvals.map((entry) => entry.transfer) ?? [];

const getCreatedByName = (createdBy: unknown): string | undefined => {
  if (!createdBy || typeof createdBy !== 'object') return undefined;
  const { name } = createdBy as { name?: unknown };
  return typeof name === 'string' ? name : undefined;
};

const getCheckPayeeName = (checkTransfer: unknown): string | undefined => {
  if (!checkTransfer || typeof checkTransfer !== 'object') return undefined;
  const { payee_name: payeeName } = checkTransfer as { payee_name?: unknown };
  return typeof payeeName === 'string' ? payeeName : undefined;
};

const ViewTransferButton = styled(Button)`
  padding: 0;
`;

const LoadingContainer = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 32px;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const AnimatedButtonContainer = styled.div<{ animationState: 'in' | 'out' | undefined }>`
  position: relative;
  display: inline-block;

  transition: all 0.3s ease;
  transform: translate(${({ animationState }) => (!!animationState ? '0, -4px' : '0, 0')});

  svg {
    --icon-color: ${({ theme }) => theme.white.background};
  }

  .button-content {
    display: inline-flex;
    align-items: center;
    line-height: 20px;
    opacity: ${({ animationState }) => (animationState === 'in' ? 0 : 1)};
    transition: opacity 0.3s ease;
    transition-delay: ${({ animationState }) => (!!animationState ? '0.3s' : '0')};
    visibility: ${({ animationState }) => (animationState === 'in' ? 'hidden' : 'visible')};
  }

  .icon {
    position: absolute;
    pointer-events: none;
    top: 50%;
    left: 50%;
    transform: translate(${({ animationState }) => (!!animationState ? '-50%, -50% ' : '-50%, -25%')})
      scale(${({ animationState }) => (animationState === 'in' ? 1.2 : animationState === 'out' ? 1.8 : 0.6)});
    opacity: ${({ animationState }) => (animationState === 'in' ? 1 : 0)};
    transition: all 0.3s ease;
  }
`;

const StyledCard = styled(Card)`
  .transfer-page-header {
    padding: 16px 16px 6px;
  }
`;

interface AnimatedButtonProps {
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  animationState?: 'in' | 'out';
  children: React.ReactNode;
  icon?: React.ReactNode;
  variant?: 'primary' | 'danger' | 'secondary';
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  onClick,
  isLoading,
  isDisabled,
  animationState,
  children,
  icon,
  variant,
}) => (
  <AnimatedButtonContainer animationState={animationState}>
    <Button variant={variant} size="small" isLoading={isLoading} isDisabled={isDisabled} onClick={onClick}>
      <span className="button-content">{children}</span>
    </Button>
    <div className="icon">{icon}</div>
  </AnimatedButtonContainer>
);

const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [role="combobox"], [role="listbox"], [contenteditable="true"]';
const ACTIVATABLE_SELECTOR = `${TEXT_ENTRY_SELECTOR}, button, a[href]`;

const targetMatches = (target: EventTarget | null, selector: string): boolean =>
  target instanceof Element && Boolean(target.closest(selector));

const getTransferBankAccountId = (transfer: UnifiedTransfer, detail: unknown): string | undefined =>
  transfer.sender_internal_account?.bank_account_id ??
  (detail as AchTransferBatch | undefined)?.import_config?.nacha?.bank_account_id ??
  undefined;

const getOptionKey = () => {
  const isMac = navigator.userAgent.includes('Mac');
  return isMac ? '⌥' : 'Alt+';
};

export const ReviewSingleDialog: React.FC<ReviewSingleDialogProps> = ({
  listQuery,
  transfers: transfersProp,
  transferStartIndex,
  currentIndex: currentIndexProp,
  totalTransfers: totalTransfersProp,
  isOpen,
  onClose,
  onCurrentTransferChange,
  unavailableIds,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const [isSubmitting, setIsSubmitting] = useState<TransferReviewDecision>();
  const [showContent, setShowContent] = useState(true);
  const [batchReview, setBatchReview] = useState<BatchReviewState>();
  const [isWide, setIsWide] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('up');
  const formMethods = useForm<ReviewTransferFormValues>();
  const comment = formMethods.watch('comment');
  const commentFocused = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(currentIndexProp);
  const [transfers, setTransfers] = useState<(UnifiedTransfer | undefined)[]>([]);
  const [isFetchingMoreTransfers, setIsFetchingMoreTransfers] = useState(false);
  const adjacentTransfers = useMemo(() => {
    const seen = new Set<string>();
    return [transfers[currentIndex], transfers[currentIndex - 1], transfers[currentIndex + 1]].filter(
      (transfer): transfer is UnifiedTransfer => {
        if (!transfer || seen.has(transfer.id)) return false;
        seen.add(transfer.id);
        return true;
      }
    );
  }, [currentIndex, transfers]);
  const transferDetailQueries = useMemo(
    () =>
      adjacentTransfers
        .map((transfer) => {
          if (transfer.type === 'ach_credit' || transfer.type === 'ach_debit') {
            return {
              id: transfer.id,
              options: transferAchGetTransferOptions({ path: { ach_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'wire') {
            return {
              id: transfer.id,
              options: transferWireGetWireTransferOptions({ path: { wire_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'check_debit') {
            return {
              id: transfer.id,
              options: transferCheckGetCheckTransferOptions({ path: { check_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'realtime') {
            return {
              id: transfer.id,
              options: transferRealtimeGetTransferOptions({ path: { realtime_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'swift') {
            return {
              id: transfer.id,
              options: transferSwiftGetTransferOptions({ path: { swift_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'ach_batch') {
            return {
              id: transfer.id,
              options: transferAchBatchGetBatchOptions({ path: { ach_transfer_batch_id: transfer.id } }),
            };
          }
          return undefined;
        })
        .filter((query): query is NonNullable<typeof query> => Boolean(query)),
    [adjacentTransfers]
  );
  const transferDetailResults = useQueries({
    queries: transferDetailQueries.map(({ options }) => ({
      ...options,
      enabled: isOpen,
    })),
  });
  const transferDetails = useMemo(() => {
    const next: Record<string, { data: unknown; isLoading: boolean }> = {};
    transferDetailQueries.forEach(({ id }, index) => {
      const result = transferDetailResults[index];
      next[id] = { data: result?.data, isLoading: Boolean(result?.isLoading || result?.isFetching) };
    });
    return next;
  }, [transferDetailQueries, transferDetailResults]);
  const bankAccountIds = useMemo(() => {
    const seen = new Set<string>();
    adjacentTransfers.forEach((transfer) => {
      const bankAccountId = getTransferBankAccountId(transfer, transferDetails[transfer.id]?.data);
      if (bankAccountId) seen.add(bankAccountId);
    });
    return Array.from(seen);
  }, [adjacentTransfers, transferDetails]);
  const bankAccountResults = useQueries({
    queries: bankAccountIds.map((bankAccountId) => ({
      ...bankAccountGetBankAccountOptions({ path: { bank_account_id: bankAccountId } }),
      enabled: isOpen,
    })),
  });
  const counterpartyIds = useMemo(() => {
    const seen = new Set<string>();
    adjacentTransfers.forEach((transfer) => {
      const counterpartyId = transfer.external_destination?.counterparty_id;
      if (counterpartyId) seen.add(counterpartyId);
    });
    return Array.from(seen);
  }, [adjacentTransfers]);
  const counterpartyResults = useQueries({
    queries: counterpartyIds.map((counterpartyId) => ({
      ...counterpartyGetCounterpartyOptions({ path: { counterparty_id: counterpartyId } }),
      enabled: isOpen,
    })),
  });
  const creators = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    // Seed from the approvals row so there's a name before the detail query resolves.
    adjacentTransfers.forEach((transfer) => {
      next[transfer.id] = getCreatedByName(transfer.created_by);
    });
    transferDetailQueries.forEach(({ id }, index) => {
      const detail = transferDetailResults[index]?.data;
      const detailCreator = detail && getCreatedByName((detail as { created_by?: unknown }).created_by);
      if (detailCreator) next[id] = detailCreator;
    });
    return next;
  }, [adjacentTransfers, transferDetailQueries, transferDetailResults]);
  const checkPayees = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    adjacentTransfers.forEach((transfer) => {
      if (transfer.type === 'check_debit') {
        next[transfer.id] = transfer.external_destination?.receiver_name;
      }
    });
    transferDetailQueries.forEach(({ id }, index) => {
      const payeeName = getCheckPayeeName(transferDetailResults[index]?.data);
      if (payeeName) next[id] = payeeName;
    });
    return next;
  }, [adjacentTransfers, transferDetailQueries, transferDetailResults]);
  const bankAccounts = useMemo(() => {
    const next: Record<string, (typeof bankAccountResults)[number]['data']> = {};
    bankAccountIds.forEach((id, index) => {
      const bankAccount = bankAccountResults[index]?.data;
      if (bankAccount) next[id] = bankAccount;
    });
    return next;
  }, [bankAccountIds, bankAccountResults]);
  const bankAccountsLoading = useMemo(() => {
    const next: Record<string, boolean> = {};
    bankAccountIds.forEach((id, index) => {
      const result = bankAccountResults[index];
      next[id] = Boolean(result?.isLoading || result?.isFetching);
    });
    return next;
  }, [bankAccountIds, bankAccountResults]);
  const counterparties = useMemo(() => {
    const next: Record<string, (typeof counterpartyResults)[number]['data']> = {};
    counterpartyIds.forEach((id, index) => {
      const counterparty = counterpartyResults[index]?.data;
      if (counterparty) next[id] = counterparty;
    });
    return next;
  }, [counterpartyIds, counterpartyResults]);
  const counterpartiesLoading = useMemo(() => {
    const next: Record<string, boolean> = {};
    counterpartyIds.forEach((id, index) => {
      const result = counterpartyResults[index];
      next[id] = Boolean(result?.isLoading || result?.isFetching);
    });
    return next;
  }, [counterpartyIds, counterpartyResults]);
  const currentData = useMemo(() => {
    const transfer = transfers[currentIndex];
    const bankAccountId = transfer && getTransferBankAccountId(transfer, transferDetails[transfer.id]?.data);
    const counterpartyId = transfer?.external_destination?.counterparty_id;

    return {
      bankAccount: bankAccountId ? bankAccounts[bankAccountId] : undefined,
      bankAccountId,
      counterparty: counterpartyId ? counterparties[counterpartyId] : undefined,
      counterpartyId,
      checkPayee: transfer?.id ? checkPayees[transfer.id] : undefined,
      creator: transfer?.id ? creators[transfer.id] : undefined,
      transfer,
    };
  }, [bankAccounts, checkPayees, counterparties, creators, currentIndex, transferDetails, transfers]);
  const currentTransferDetailResult = useMemo(() => {
    if (currentData.transfer?.type !== 'swift') return;
    const detailIndex = transferDetailQueries.findIndex(({ id }) => id === currentData.transfer?.id);
    return detailIndex >= 0 ? transferDetailResults[detailIndex] : undefined;
  }, [currentData.transfer, transferDetailQueries, transferDetailResults]);
  const currentSwiftTransferDetail = currentTransferDetailResult?.data as SwiftTransfer | undefined;
  const isCurrentInternationalWireApprovalBlocked = isInternationalWireApprovalBlocked(
    currentData.transfer?.type,
    currentTransferDetailResult
      ? {
          hasData: Boolean(currentSwiftTransferDetail),
          isError: currentTransferDetailResult.isError,
          isFetching: currentTransferDetailResult.isFetching,
          isLoading: currentTransferDetailResult.isLoading,
        }
      : undefined
  );
  const reviewQuoteTransfer = useMemo<ReviewQuoteTransfer | undefined>(() => {
    const transfer = currentData.transfer;
    if (transfer?.type !== 'swift') return;
    if (isApprovalHold(transfer.status)) return;
    if (isCurrentInternationalWireApprovalBlocked || !currentSwiftTransferDetail) return;
    return {
      amount: currentSwiftTransferDetail.amount,
      currencyCode: currentSwiftTransferDetail.currency_code,
      fxQuoteId: currentSwiftTransferDetail.fx_quote_id,
      id: transfer.id,
      instructedAmount: currentSwiftTransferDetail.instructed_amount ?? undefined,
      instructedCurrencyCode: currentSwiftTransferDetail.instructed_currency_code,
    };
  }, [currentData.transfer, currentSwiftTransferDetail, isCurrentInternationalWireApprovalBlocked]);
  const {
    isFXTransfer: isCurrentFXTransfer,
    isLoading: isFXQuoteLoading,
    quote: currentFXQuote,
    refreshQuote: requestFXQuote,
  } = useInternationalWireReviewQuote(reviewQuoteTransfer, isOpen);
  const isCurrentBatch = Boolean(currentData.transfer && isBatchTransfer(currentData.transfer));
  const emptyState = transfers.length === 0;
  const isLoadingAdditionalData =
    isOpen &&
    !emptyState &&
    !!currentData.transfer &&
    (Boolean(transferDetails[currentData.transfer.id]?.isLoading) ||
      (!!currentData.bankAccountId && !currentData.bankAccount && bankAccountsLoading[currentData.bankAccountId]) ||
      (!!currentData.counterpartyId && !currentData.counterparty && counterpartiesLoading[currentData.counterpartyId]));
  useEffect(() => {
    if (!isLoadingAdditionalData) {
      setShowContent(true);
    }
  }, [isLoadingAdditionalData]);
  useEffect(() => {
    if (!isOpen) return;
    commentFocused.current = false;
    setBatchReview(undefined);
    setIsWide(false);
    const arr: (UnifiedTransfer | undefined)[] = Array(totalTransfersProp).fill(undefined);
    transfersProp.forEach((transfer, i) => {
      const slot = transferStartIndex + i;
      if (slot < arr.length) arr[slot] = transfer;
    });
    setTransfers(arr);
    setCurrentIndex(currentIndexProp);
  }, [isOpen]);

  const fetchSlot = useCallback(
    async (idx: number) => {
      setIsFetchingMoreTransfers(true);
      try {
        let cursorIdx = idx - 1;
        const snapshot = transfers;
        while (cursorIdx >= 0 && !snapshot[cursorIdx]) cursorIdx--;
        const cursor = cursorIdx >= 0 ? snapshot[cursorIdx]!.id : undefined;

        const response = await approvalsListApprovals({
          query: { ...listQuery, status: 'pending', starting_after: cursor, limit: MODAL_PAGE_SIZE },
          throwOnError: true,
        });
        const fetched = extractTransfers(response.data);
        const startIdx = cursorIdx + 1;
        setTransfers((previous) => {
          const arr = previous.slice();
          fetched.forEach((transfer, i) => {
            if (startIdx + i < arr.length) arr[startIdx + i] = transfer;
          });
          return arr;
        });
      } finally {
        setIsFetchingMoreTransfers(false);
      }
    },
    [listQuery, transfers]
  );

  const handlePrevious = useCallback(() => {
    const prevIndex = currentIndex - 1;
    if (prevIndex < 0 || !transfers[prevIndex]) return;

    commentFocused.current = false;
    setSlideDirection('down');
    setShowContent(false);

    setTimeout(() => {
      formMethods.reset();
      setCurrentIndex(prevIndex);
      setTimeout(() => setShowContent(true), 100);
    }, 100);
  }, [currentIndex, formMethods, transfers]);

  const handleNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= transfers.length) return;

    commentFocused.current = false;
    setSlideDirection('up');
    setShowContent(false);

    setTimeout(async () => {
      if (!transfers[nextIndex]) await fetchSlot(nextIndex);
      formMethods.reset();
      setCurrentIndex(nextIndex);
      setTimeout(() => setShowContent(true), 100);
    }, 100);
  }, [currentIndex, fetchSlot, formMethods, transfers]);

  useEffect(() => {
    if (!isOpen) return;
    onCurrentTransferChange?.(currentData.transfer?.id);
  }, [currentData.transfer?.id, isOpen, onCurrentTransferChange]);
  const isBodyLoading = !currentData.transfer || isLoadingAdditionalData || isFetchingMoreTransfers;
  const [isRevealed, setIsRevealed] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setIsRevealed(false);
      return undefined;
    }
    if (isRevealed) return undefined;
    if (!isBodyLoading) {
      setIsRevealed(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setIsRevealed(true), REVEAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isBodyLoading, isOpen, isRevealed]);
  const isVisible = isOpen && isRevealed;
  const isCurrentUnavailable = Boolean(currentData.transfer && unavailableIds?.has(currentData.transfer.id));
  const isActionDisabled = emptyState || isBodyLoading || isCurrentUnavailable;

  const [successAnimation, setSuccessAnimation] = useState<'in' | 'out' | undefined>(undefined);
  const [rejectAnimation, setRejectAnimation] = useState<'in' | 'out' | undefined>(undefined);

  const handleSubmit = useCallback(
    async (decision: TransferReviewDecision) => {
      if (!currentData.transfer) return;
      if (decision === TransferReviewDecision.Approved && isCurrentInternationalWireApprovalBlocked) return;
      if (decision === TransferReviewDecision.Approved && isCurrentFXTransfer && !currentFXQuote) return;

      setIsSubmitting(decision);

      try {
        const reviewBatch = await onSubmit(
          currentData.transfer.id,
          currentData.transfer.type as TransferEntryType,
          currentData.transfer.status,
          {
            comment,
            decision,
            fx_quote_id:
              decision === TransferReviewDecision.Approved && isCurrentFXTransfer ? currentFXQuote?.id : undefined,
          }
        );

        if (reviewBatch?.id) {
          setBatchReview({
            id: reviewBatch.id,
            decision: reviewBatch.decision,
            count: currentData.transfer.batch_transfer_count ?? reviewBatch.total_count,
          });
          return;
        }

        if (decision === TransferReviewDecision.Approved) {
          setSuccessAnimation('in');
          await new Promise((resolve) => setTimeout(resolve, 600));
          setSuccessAnimation('out');
          await new Promise((resolve) => setTimeout(resolve, 300));
        } else if (decision === TransferReviewDecision.Rejected) {
          setRejectAnimation('in');
          await new Promise((resolve) => setTimeout(resolve, 600));
          setRejectAnimation('out');
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } catch (error) {
        if (
          decision === TransferReviewDecision.Approved &&
          isCurrentFXTransfer &&
          (error as { code?: string })?.code === 'fx_quote_expired'
        ) {
          await requestFXQuote();
          return;
        }
        throw error;
      } finally {
        setIsSubmitting(undefined);
        setSuccessAnimation(undefined);
        setRejectAnimation(undefined);
      }

      setTransfers((previous) => {
        const updated = previous.slice();
        updated.splice(currentIndex, 1);
        setCurrentIndex((existingIndex) => Math.min(existingIndex, Math.max(updated.length - 1, 0)));
        return updated;
      });
      formMethods.reset();
    },
    [
      comment,
      currentData,
      currentFXQuote,
      currentIndex,
      formMethods,
      isCurrentFXTransfer,
      isCurrentInternationalWireApprovalBlocked,
      onSubmit,
      requestFXQuote,
    ]
  );

  const dismissBatchReview = useCallback(
    (direction: 'stay' | 'previous' = 'stay') => {
      setBatchReview(undefined);
      setIsWide(false);
      setTransfers((previous) => {
        const updated = previous.slice();
        updated.splice(currentIndex, 1);
        setCurrentIndex((existingIndex) => {
          const target = direction === 'previous' ? existingIndex - 1 : existingIndex;
          return Math.min(Math.max(target, 0), Math.max(updated.length - 1, 0));
        });
        return updated;
      });
      formMethods.reset();
    },
    [currentIndex, formMethods]
  );

  const continueAfterBatchReview = useCallback(() => dismissBatchReview('stay'), [dismissBatchReview]);

  const goPrevious = useCallback(() => {
    if (batchReview) {
      setSlideDirection('down');
      dismissBatchReview('previous');
      return;
    }
    handlePrevious();
  }, [batchReview, dismissBatchReview, handlePrevious]);

  const goNext = useCallback(() => {
    if (batchReview) {
      setSlideDirection('up');
      continueAfterBatchReview();
      return;
    }
    handleNext();
  }, [batchReview, continueAfterBatchReview, handleNext]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isAltOption = e.altKey;

      if (e.defaultPrevented || isLoadingAdditionalData || isSubmitting || isFetchingMoreTransfers) return;
      if (!isAltOption && targetMatches(e.target, e.key === 'Enter' ? ACTIVATABLE_SELECTOR : TEXT_ENTRY_SELECTOR))
        return;

      if (batchReview) {
        if (e.key === 'ArrowRight' || e.key === 'Enter') {
          e.preventDefault();
          goNext();
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goPrevious();
        }
        return;
      }

      if (isAltOption) {
        if (isActionDisabled) return;
        if (e.code === 'KeyA') {
          e.preventDefault();
          handleSubmit(TransferReviewDecision.Approved);
        }
        if (e.code === 'KeyR') {
          e.preventDefault();
          handleSubmit(TransferReviewDecision.Rejected);
        }
      } else if (!commentFocused.current) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          goNext();
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goPrevious();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isActionDisabled,
    isVisible,
    isLoadingAdditionalData,
    isSubmitting,
    isFetchingMoreTransfers,
    handleSubmit,
    goNext,
    goPrevious,
    batchReview,
  ]);

  return (
    <ReviewDialog
      isOpen={isVisible}
      onClose={onClose}
      showCloseButton={false}
      isWide={isWide}
      className="pt-[22px] sm:max-w-[640px]"
    >
      <DialogHeader className="relative gap-1 pb-1">
        <DialogTitle>Review Transfer</DialogTitle>
        <DialogDescription className={cn(!batchReview && 'sr-only')}>
          {batchReview ? BACKGROUND_NOTICE : 'Approve or reject transfers pending approval.'}
        </DialogDescription>
        <Fade
          show={!emptyState}
          direction={slideDirection === 'up' ? 'right' : 'left'}
          className="sm:absolute sm:right-0 sm:top-0 sm:flex sm:h-5 sm:items-center"
        >
          <PageCounter current={currentIndex + 1} total={transfers.length} onPrevious={goPrevious} onNext={goNext} />
        </Fade>
      </DialogHeader>

      <AnimatedHeight freeze={isBodyLoading || !showContent}>
        {batchReview ? (
          <div className="flex min-w-0 flex-col justify-center">
            <ReviewBatchSummary
              decision={batchReview.decision}
              requestedCount={batchReview.count}
              reviewBatchId={batchReview.id}
              onHasAttentionChange={setIsWide}
            />
          </div>
        ) : emptyState ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center">
            <Text size={18} weight="medium" color="foreground">
              All caught up
            </Text>
            <Text size={14} color="light">
              There are no more transfers to review
            </Text>
          </div>
        ) : (
          <div className="relative">
            <LoadingContainer show={isBodyLoading}>
              <LogoLoading />
            </LoadingContainer>
            <Fade
              key={currentIndex}
              show={!isLoadingAdditionalData && showContent}
              distance={16}
              direction={slideDirection === 'up' ? 'right' : 'left'}
            >
              <div className="grid gap-6">
                <StyledCard>
                  <TransferHeader
                    className="transfer-page-header"
                    metaAction={
                      <ViewTransferButton
                        icon={<Icon.ChevronRightLarge />}
                        iconRight
                        variant="subtle"
                        onClick={() =>
                          currentData.transfer &&
                          navigate(
                            getTransferURL(currentData.transfer.id, currentData.transfer.type as TransferEntryType)
                          )
                        }
                      >
                        View
                      </ViewTransferButton>
                    }
                    amount={
                      isCurrentBatch
                        ? Math.abs(currentData.transfer?.amount ?? 0)
                        : (currentFXQuote?.sellAmount ?? currentData.transfer?.amount ?? 0)
                    }
                    createdAt={currentData.transfer?.created_at ?? undefined}
                    currencyCode={currentFXQuote?.sellCurrencyCode ?? currentData.transfer?.currency_code}
                    direction={{ type: 'outgoing' }}
                    isAdding={
                      isCurrentBatch && currentData.transfer
                        ? getBatchNetAmount(currentData.transfer) > 0
                        : currentData.transfer?.type
                          ? convertTransferAmount(
                              currentData.transfer.type as TransferEntryType,
                              currentData.transfer.is_incoming
                            ) > 0
                          : undefined
                    }
                    settledCurrencyAmount={currentSwiftTransferDetail?.settled_amount ?? undefined}
                    settledCurrencyCode={currentSwiftTransferDetail?.settled_currency_code}
                    transferType={currentData.transfer ? (currentData.transfer.type as TransferEntryType) : 'wire'}
                  />
                  {isCurrentBatch ? (
                    <>
                      {currentData.bankAccountId && (
                        <CopyRow
                          label="Account"
                          value={
                            <EntityLink
                              label={currentData.bankAccount?.description || currentData.bankAccountId}
                              onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${currentData.bankAccountId}`)}
                            />
                          }
                        />
                      )}
                      <CopyRow label="Transfers" value={String(currentData.transfer?.batch_transfer_count ?? '-')} />
                      {currentData.creator && <CopyRow label="Created By" value={currentData.creator} />}
                      <CopyRow label="Description" value={currentData.transfer?.description || '-'} />
                    </>
                  ) : (
                    <>
                      <CopyRow
                        label="Account"
                        value={
                          currentData.bankAccountId ? (
                            <EntityLink
                              label={currentData.bankAccount?.description || currentData.bankAccountId}
                              onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${currentData.bankAccountId}`)}
                            />
                          ) : (
                            '-'
                          )
                        }
                      />
                      <CopyRow
                        label={currentData.transfer?.type === 'check_debit' ? 'Payee' : 'Counterparty'}
                        value={
                          currentData.counterpartyId && !currentData.checkPayee ? (
                            <EntityLink
                              label={currentData.counterparty?.name || currentData.counterpartyId}
                              onClick={() => navigate(`${ROUTE.COUNTERPARTIES}/edit/${currentData.counterpartyId}`)}
                            />
                          ) : (
                            currentData.checkPayee || currentData.counterparty?.name || '-'
                          )
                        }
                      />
                      <CopyRow label="Created By" value={currentData.creator || '-'} />
                      <CopyRow label="Description" value={currentData.transfer?.description || '-'} />
                    </>
                  )}
                  {isCurrentFXTransfer &&
                    getInternationalWireReviewFXQuoteDetails({
                      context: 'copy-row',
                      isLoading: isFXQuoteLoading,
                      isSandbox: !!isSandbox,
                      onRetry: requestFXQuote,
                      quote: currentFXQuote,
                    }).map(({ label, value }) => (
                      <CopyRow
                        key={label}
                        label={label}
                        value={
                          typeof value === 'string' ? value : <ReviewQuoteFieldWrapper>{value}</ReviewQuoteFieldWrapper>
                        }
                      />
                    ))}
                </StyledCard>
                <div className="grid gap-2">
                  <Label>Comment</Label>
                  <Input
                    value={comment ?? ''}
                    isDisabled={!!isSubmitting}
                    placeholder="Add a comment..."
                    onChange={(value) => formMethods.setValue('comment', value)}
                    onFocus={() => (commentFocused.current = true)}
                    onBlur={() => (commentFocused.current = false)}
                  />
                </div>
              </div>
            </Fade>
          </div>
        )}
      </AnimatedHeight>

      <DialogFooter className="flex-row items-center justify-between sm:justify-between">
        {batchReview ? (
          <>
            <AnimatedButton onClick={onClose} variant="secondary">
              Close
            </AnimatedButton>
            <Button size="small" onClick={continueAfterBatchReview}>
              Continue
              <Icon.ChevronRightLarge />
            </Button>
          </>
        ) : (
          <>
            <AnimatedButton onClick={onClose} variant="secondary">
              Cancel
            </AnimatedButton>
            <Flex alignItems="center" flexDirection="row" gap={8} justifyContent="flex-end">
              <AnimatedButton
                onClick={() => handleSubmit(TransferReviewDecision.Rejected)}
                isLoading={isSubmitting === TransferReviewDecision.Rejected && !rejectAnimation}
                isDisabled={isActionDisabled || isSubmitting === TransferReviewDecision.Approved}
                animationState={rejectAnimation}
                icon={<Icon.Cross />}
                variant="danger"
              >
                Reject <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>{getOptionKey()}R</span>
              </AnimatedButton>
              <AnimatedButton
                onClick={() => handleSubmit(TransferReviewDecision.Approved)}
                isLoading={isSubmitting === TransferReviewDecision.Approved && !successAnimation}
                isDisabled={
                  isActionDisabled ||
                  isSubmitting === TransferReviewDecision.Rejected ||
                  isCurrentInternationalWireApprovalBlocked ||
                  (isCurrentFXTransfer && (isFXQuoteLoading || !currentFXQuote))
                }
                animationState={successAnimation}
                icon={<Icon.Checkmark />}
              >
                Approve <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>{getOptionKey()}A</span>
              </AnimatedButton>
            </Flex>
          </>
        )}
      </DialogFooter>
    </ReviewDialog>
  );
};
